import { addMonths, parseISO, startOfMonth } from 'date-fns'
import { getPeriodStartEnd } from '@dolfin/utils/dates'
import { Diagram, DolfinDiagram } from '../../diagram'
import { DBFunctionReturns } from '../../custom_types'
import { Enums, TablesInsert } from '../../db_types'
import { Tables } from '../../db_types'
import { evaluateBranchByUserLogic } from '../../doesVersionApplyToUser'
import LogicEvaluator from '../../logic-evaluator'
import {
  extractFormulaSourceFieldKeys,
  getRecordFieldValue,
  type RecordFieldReference
} from '../../record'
import ProfilesDomain from '../profiles'
import {
  DolfinRpc,
  MetricSourceRecordsPage,
  TargetContributingSourceRecordsPageParams
} from '../../rpc'
import { AnyTypedNode, isBranchByUserNode, TypedNode } from '../../node/types'

export type MetricSourceRecord = {
  record_id: number
  record_name: string | null
  record_status: string | null
  record_date: string | null
  record_value: number | null
  deal_name: string | null
  pending_processing: boolean
  included: boolean
  metric_value: number | null
  source_type: Enums<'plan_source'>
  source_record: Record<string, unknown> | null
}

export type MetricArchiveMode = 'not_archived' | 'not_archived_yet'

export type GetPendingProcessingCountParams = {
  metricId: number
  profileId: string
  periodStart: string
  periodEnd: string
  organizationId: number
  teamIds?: number[]
}

export type GetMetricSourceRecordsParams = {
  metricId: number
  profileId: string
  periodStart: string
  periodEnd: string
  organizationId: number
  teamIds?: number[]
  targetCurrency?: string
}

export type GetMetricsValuesParams = {
  metricIds?: number[]
  startDate: string
  endDate: string
  organizationId: number
  profileIds?: string[]
  teamIds?: number[]
  sourceId?: number
  metricsToUpsert?: TablesInsert<'metric_values'>[]
  metricsToExclude?: Pick<
    TablesInsert<'metric_values'>,
    | 'metric_id'
    | 'deal_id'
    | 'activity_id'
    | 'invoice_id'
    | 'kpi_id'
    | 'line_item_id'
    | 'deal_line_item_id'
    | 'custom_record_id'
  >[]
  groupBy?: string
  cumulative?: boolean
  targetCurrency?: string
  targetId?: number
  requestingProfileId?: string
}

export type GetOrganizationMetricsValuesParams = Omit<
  GetMetricsValuesParams,
  'profileIds' | 'teamIds'
>

type UserViewPerformanceMetricEligibility = {
  eligibleMetricIds: Set<number>
  perPeriod: Map<
    string,
    {
      eligibleMetricIds: Set<number>
      targetMetricIds: Set<number>
    }
  >
}

type UserViewPerformanceMetricEligibilityOverrides = Map<string, Set<number>>

export type MetricUsage = {
  compensationRules: (Tables<'compensation_rules'> & {
    compensation_rule_versions: (Tables<'compensation_rule_versions'> & {
      compensation_rule_version_metrics: Tables<'compensation_rule_version_metrics'>[]
    })[]
  })[]
  targets: Tables<'targets'>[]
  challenges: Tables<'challenges'>[]
  creditSetups: Tables<'credit_setups'>[]
  isBeingUsed: boolean
}

export type GetDailyMetricsParams = {
  organizationId: number
  startDate: string
  endDate: string
  metricIds?: number[]
  teamIds?: number[]
  rpcClient: DolfinRpc
}

export type GetMetricsByPeriodParams = Parameters<DolfinRpc['getMetricsByPeriod']>[0]
export type GetMetricValueDimensionsParams = Parameters<
  DolfinRpc['getCompensationHistoryMetricBuckets']
>[0]

type UserCompensationRuleRow = Awaited<ReturnType<DolfinRpc['getUserCompensationRules']>>[number]
type DiagramLike = {
  nodes?: unknown
  edges?: unknown
}

const NON_RECORD_FILTER_VARIABLES = new Set([
  'compensation_value',
  'processing_day',
  'weighted_target_achievement'
])

const getFilterFieldNames = (value: unknown, fields: Set<string>) => {
  if (Array.isArray(value)) {
    value.forEach(item => getFilterFieldNames(item, fields))
    return
  }

  if (!value || typeof value !== 'object') {
    return
  }

  const record = value as Record<string, unknown>
  if (typeof record.var === 'string') {
    fields.add(record.var)
  }

  Object.values(record).forEach(item => getFilterFieldNames(item, fields))
}

const isRecordFieldName = (fieldName: string) =>
  fieldName.length > 0 &&
  !fieldName.startsWith('user.') &&
  !fieldName.startsWith('period.') &&
  !fieldName.startsWith('target') &&
  !fieldName.startsWith('metric_') &&
  !NON_RECORD_FILTER_VARIABLES.has(fieldName)

const getFormulaFieldNames = (metric: Tables<'metrics'>) => {
  const formula = metric.formula?.trim()
  if (!formula || formula === '1' || !metric.source) {
    return []
  }

  if (
    /^(?:\$?[a-z_][a-z0-9_]*|(?:deal|invoice|activity|line_item|deal_line_item|custom_record)\.\$?[a-z_][a-z0-9_]*)$/i.test(
      formula
    )
  ) {
    return [formula]
  }

  return extractFormulaSourceFieldKeys({
    source: metric.source,
    formula
  }).map(field => (field.startsWith('meta.') ? `$${field.slice(5)}` : field))
}

export default class Metric {
  private rpcClient: DolfinRpc
  private readonly metric?: Tables<'metrics'>

  constructor({ rpcClient, metric }: { rpcClient: DolfinRpc; metric?: Tables<'metrics'> }) {
    this.rpcClient = rpcClient
    this.metric = metric
  }

  private static getUserViewPerformanceMetricPeriods(startDate: string, endDate: string) {
    const periods: Array<{ start: string; end: string; key: string }> = []
    const end = parseISO(endDate)

    for (
      let cursor = startOfMonth(parseISO(startDate));
      cursor <= end;
      cursor = addMonths(cursor, 1)
    ) {
      const period = getPeriodStartEnd('month', cursor)
      periods.push({
        start: period.start,
        end: period.end,
        key: period.start
      })
    }

    return periods
  }

  async getValues({
    metricIds = [],
    startDate,
    endDate,
    organizationId,
    profileIds,
    teamIds,
    sourceId,
    metricsToUpsert,
    metricsToExclude,
    groupBy,
    cumulative,
    targetCurrency
  }: GetMetricsValuesParams): Promise<DBFunctionReturns<'get_metrics_values'>> {
    if (
      Metric.isUserViewPerformanceMetricsRequest({
        metricIds,
        profileIds,
        teamIds,
        sourceId,
        metricsToUpsert,
        metricsToExclude,
        groupBy
      })
    ) {
      const profileId = profileIds?.[0]
      if (!profileId) {
        return this.rpcClient.getMetricsValues(
          metricIds,
          startDate,
          endDate,
          organizationId,
          profileIds,
          teamIds,
          sourceId,
          metricsToUpsert,
          metricsToExclude,
          groupBy,
          cumulative,
          targetCurrency
        )
      }

      const eligibility = await this.getUserViewPerformanceMetricEligibility({
        profileId,
        startDate,
        endDate,
        organizationId
      })

      if (eligibility.eligibleMetricIds.size === 0) {
        return []
      }

      const data = await this.rpcClient.getMetricsValues(
        Array.from(eligibility.eligibleMetricIds).sort((a, b) => a - b),
        startDate,
        endDate,
        organizationId,
        profileIds,
        teamIds,
        sourceId,
        metricsToUpsert,
        metricsToExclude,
        groupBy,
        cumulative,
        targetCurrency
      )

      return Metric.filterUserViewPerformanceMetrics(data, eligibility)
    }

    const data = await this.rpcClient.getMetricsValues(
      metricIds,
      startDate,
      endDate,
      organizationId,
      profileIds,
      teamIds,
      sourceId,
      metricsToUpsert,
      metricsToExclude,
      groupBy,
      cumulative,
      targetCurrency
    )

    return data
  }

  async getOrganizationValues({
    metricIds = [],
    startDate,
    endDate,
    organizationId,
    sourceId,
    metricsToUpsert,
    metricsToExclude,
    groupBy,
    cumulative,
    targetCurrency
  }: GetOrganizationMetricsValuesParams): Promise<
    DBFunctionReturns<'get_organization_metrics_values'>
  > {
    return this.rpcClient.getOrganizationMetricsValues({
      metricIds,
      startDate,
      endDate,
      organizationId,
      sourceId,
      metricsToUpsert,
      metricsToExclude,
      groupBy,
      cumulative,
      targetCurrency
    })
  }

  async getUserViewSummaryValues({
    profileId,
    startDate,
    endDate,
    organizationId,
    targetMetricIdsByPeriod
  }: {
    profileId: string
    startDate: string
    endDate: string
    organizationId: number
    targetMetricIdsByPeriod: UserViewPerformanceMetricEligibilityOverrides
  }): Promise<DBFunctionReturns<'get_metrics_values'>> {
    const eligibility = await this.getUserViewPerformanceMetricEligibility({
      profileId,
      startDate,
      endDate,
      organizationId,
      targetMetricIdsByPeriod
    })

    if (eligibility.eligibleMetricIds.size === 0) {
      return []
    }

    const [period] = Metric.getUserViewPerformanceMetricPeriods(startDate, endDate)
    if (!period) {
      return []
    }

    const data = await this.rpcClient.getMetricsValues(
      Array.from(eligibility.eligibleMetricIds).sort((a, b) => a - b),
      startDate,
      endDate,
      organizationId,
      [profileId]
    )

    return Metric.filterUserViewSummaryMetrics(data, eligibility, period.key)
  }

  private static isUserViewPerformanceMetricsRequest({
    metricIds = [],
    profileIds,
    teamIds,
    sourceId,
    metricsToUpsert,
    metricsToExclude,
    groupBy
  }: Pick<
    GetMetricsValuesParams,
    | 'metricIds'
    | 'profileIds'
    | 'teamIds'
    | 'sourceId'
    | 'metricsToUpsert'
    | 'metricsToExclude'
    | 'groupBy'
  >) {
    return (
      metricIds.length === 0 &&
      profileIds?.length === 1 &&
      (!teamIds || teamIds.length === 0) &&
      !sourceId &&
      (!metricsToUpsert || metricsToUpsert.length === 0) &&
      (!metricsToExclude || metricsToExclude.length === 0) &&
      groupBy === 'month'
    )
  }

  private async getUserViewPerformanceMetricEligibility({
    profileId,
    startDate,
    endDate,
    organizationId,
    targetMetricIdsByPeriod
  }: {
    profileId: string
    startDate: string
    endDate: string
    organizationId: number
    targetMetricIdsByPeriod?: UserViewPerformanceMetricEligibilityOverrides
  }): Promise<UserViewPerformanceMetricEligibility> {
    const profileData = await new ProfilesDomain({ rpcClient: this.rpcClient }).getProfileData(
      profileId,
      organizationId
    )

    const periodEligibility = await Promise.all(
      Metric.getUserViewPerformanceMetricPeriods(startDate, endDate).map(async period => {
        const rulesPromise = this.rpcClient.getUserCompensationRules(
          profileId,
          organizationId,
          period.start,
          period.end
        )
        const targetMetricIds = targetMetricIdsByPeriod?.get(period.key)

        const [rules, targetAchievements] = await Promise.all([
          rulesPromise,
          targetMetricIds
            ? Promise.resolve(null)
            : this.rpcClient.getUserTargetsAchievementInPeriod(
                profileId,
                period.start,
                period.end,
                organizationId
              )
        ])

        const resolvedTargetMetricIds =
          targetMetricIds ??
          new Set(
            (targetAchievements ?? [])
              .filter(targetAchievement => targetAchievement.credit_setup_id == null)
              .map(targetAchievement => targetAchievement.metric_id)
              .filter((metricId): metricId is number => metricId != null)
          )

        const ruleVersionIds = Array.from(
          new Set(
            rules
              .map(rule => rule.version_id)
              .filter((ruleVersionId): ruleVersionId is number => ruleVersionId != null)
          )
        ).sort((a, b) => a - b)
        const ruleMetricIds =
          ruleVersionIds.length > 0
            ? await this.rpcClient.getCompensationRuleVersionMetricIds(ruleVersionIds)
            : []
        const reachableDirectMetricIds = Array.from(
          rules.reduce((metricIds, rule) => {
            Metric.getReachableDirectMetricIdsForRule({
              rule,
              profileId,
              profileData,
              period
            }).forEach(metricId => metricIds.add(metricId))

            return metricIds
          }, new Set<number>())
        ).sort((a, b) => a - b)
        const eligibleMetricIds = new Set(
          reachableDirectMetricIds.filter(
            metricId => ruleMetricIds.includes(metricId) && !resolvedTargetMetricIds.has(metricId)
          )
        )

        return [
          period.key,
          {
            eligibleMetricIds,
            targetMetricIds: resolvedTargetMetricIds
          }
        ] as const
      })
    )

    const perPeriod = new Map(periodEligibility)
    const eligibleMetricIds = new Set(
      periodEligibility.flatMap(([, eligibility]) => Array.from(eligibility.eligibleMetricIds))
    )

    return {
      eligibleMetricIds,
      perPeriod
    }
  }

  private static filterUserViewPerformanceMetrics(
    data: DBFunctionReturns<'get_metrics_values'>,
    { perPeriod }: UserViewPerformanceMetricEligibility
  ) {
    const seenMetricKeys = new Set<string>()

    return data.filter(metric => {
      const periodEligibility = perPeriod.get(metric.period)

      if (!periodEligibility) {
        return false
      }

      const metricKey = `${metric.metric_id}:${metric.period}`

      if (seenMetricKeys.has(metricKey)) {
        return false
      }

      if (
        !periodEligibility.eligibleMetricIds.has(metric.metric_id) ||
        periodEligibility.targetMetricIds.has(metric.metric_id)
      ) {
        return false
      }

      const isVisible = metric.aggregated_value !== null

      if (isVisible) {
        seenMetricKeys.add(metricKey)
      }

      return isVisible
    })
  }

  private static filterUserViewSummaryMetrics(
    data: DBFunctionReturns<'get_metrics_values'>,
    { perPeriod }: UserViewPerformanceMetricEligibility,
    periodKey: string
  ) {
    const periodEligibility = perPeriod.get(periodKey)

    if (!periodEligibility) {
      return []
    }

    const seenMetricIds = new Set<number>()

    return data.filter(metric => {
      if (seenMetricIds.has(metric.metric_id)) {
        return false
      }

      if (
        !periodEligibility.eligibleMetricIds.has(metric.metric_id) ||
        periodEligibility.targetMetricIds.has(metric.metric_id) ||
        metric.aggregated_value === null
      ) {
        return false
      }

      seenMetricIds.add(metric.metric_id)
      return true
    })
  }

  private static getReachableDirectMetricIdsForRule({
    rule,
    profileId,
    profileData,
    period
  }: {
    rule: UserCompensationRuleRow
    profileId: string
    profileData: Tables<'organizations_profiles_data'>[]
    period: { start: string; end: string }
  }) {
    const diagram = Metric.getRuleDiagram(rule.diagram)
    const startNode = diagram.getStartNode()

    if (!startNode) {
      return new Set(diagram.nodes.flatMap(node => Metric.getDirectMetricIdsForNode(node)))
    }

    const reachableMetricIds = new Set<number>()
    const visitedNodes = new Set<string>()

    const traverse = (node: AnyTypedNode | null) => {
      if (!node || visitedNodes.has(node.id)) {
        return
      }

      visitedNodes.add(node.id)
      Metric.getDirectMetricIdsForNode(node).forEach(metricId => reachableMetricIds.add(metricId))

      const outgoingEdges = diagram.outPaths(node)
      if (outgoingEdges.length === 0) {
        return
      }

      if (isBranchByUserNode(node)) {
        const matchedPathNumber = Metric.getBranchByUserPathNumber({
          node,
          profileId,
          profileData,
          period: {
            start: rule.period_start ?? period.start,
            end: rule.period_end ?? period.end
          }
        })

        if (matchedPathNumber < 0) {
          return
        }

        outgoingEdges
          .filter(edge => edge.data?.pathNumber === matchedPathNumber)
          .forEach(edge => traverse(diagram.getNextNode(edge)))

        return
      }

      outgoingEdges.forEach(edge => traverse(diagram.getNextNode(edge)))
    }

    traverse(startNode)

    return reachableMetricIds
  }

  private static getRuleDiagram(diagramJson: UserCompensationRuleRow['diagram']) {
    const diagramLike =
      diagramJson && typeof diagramJson === 'object' && !Array.isArray(diagramJson)
        ? (diagramJson as DiagramLike)
        : {}

    return new DolfinDiagram({
      nodes: Array.isArray(diagramLike.nodes) ? (diagramLike.nodes as Diagram['nodes']) : [],
      edges: Array.isArray(diagramLike.edges) ? (diagramLike.edges as Diagram['edges']) : []
    })
  }

  private static getBranchByUserPathNumber({
    node,
    profileId,
    profileData,
    period
  }: {
    node: TypedNode<'branch-by-user'>
    profileId: string
    profileData: Tables<'organizations_profiles_data'>[]
    period: { start: string; end: string }
  }) {
    const filters = Array.isArray(node.data.formData?.filters) ? node.data.formData.filters : []

    for (const [index, filter] of filters.entries()) {
      if (!filter) {
        continue
      }

      if (
        !filter.data ||
        evaluateBranchByUserLogic(filter, profileId, profileData, period, period)
      ) {
        return index
      }
    }

    return -1
  }

  private static getDirectMetricIdsForNode(node: AnyTypedNode): number[] {
    const formData =
      node.data && typeof node.data === 'object' && 'formData' in node.data
        ? node.data.formData
        : null

    if (!formData || typeof formData !== 'object') {
      return []
    }

    const metricIds = new Set<number>()
    const metricReference =
      'reference_type' in formData &&
      formData.reference_type === 'metric' &&
      'metric' in formData &&
      formData.metric &&
      typeof formData.metric === 'object'
        ? formData.metric
        : null

    if (metricReference && typeof metricReference.id === 'number') {
      metricIds.add(metricReference.id)
    }

    const calculateOverMetric =
      'calculate_over_metric' in formData &&
      formData.calculate_over_metric &&
      typeof formData.calculate_over_metric === 'object'
        ? formData.calculate_over_metric
        : null

    if (calculateOverMetric && typeof calculateOverMetric.id === 'number') {
      metricIds.add(calculateOverMetric.id)
    }

    const evaluator = new LogicEvaluator('deal', {})
    const filters = 'filters' in formData ? formData.filters : null

    if (filters && typeof filters === 'object' && !Array.isArray(filters)) {
      evaluator.getMetricIds(filters).forEach(metricId => metricIds.add(metricId))
    }

    if (Array.isArray(filters)) {
      filters.forEach(filter => {
        const filterData =
          filter &&
          typeof filter === 'object' &&
          'data' in filter &&
          filter.data &&
          typeof filter.data === 'object' &&
          !Array.isArray(filter.data)
            ? filter.data
            : null

        if (!filterData) {
          return
        }

        evaluator.getMetricIds(filterData).forEach(metricId => metricIds.add(metricId))
      })
    }

    return Array.from(metricIds)
  }

  static getColumnFromMetricFormula(
    metricFormula: string,
    metricSource: string | null,
    currentSource: string | null
  ): string {
    if (!metricSource || !currentSource) return metricFormula

    const metricSourceIsFromParentTable =
      (metricSource === 'deal' && currentSource === 'invoice') ||
      (metricSource === 'invoice' && currentSource === 'line_item') ||
      (metricSource === 'deal' && currentSource === 'activity') ||
      (metricSource === 'deal' && currentSource === 'deal_line_item')

    if (
      metricSource === 'deal' &&
      currentSource === 'deal_line_item' &&
      metricFormula.startsWith('deal.')
    ) {
      return metricFormula
    }

    return metricSourceIsFromParentTable ? `${metricSource}.${metricFormula}` : metricFormula
  }

  static getNonArchived({
    rpcClient,
    organizationId,
    mode = 'not_archived',
    referenceDate
  }: {
    rpcClient: DolfinRpc
    organizationId: number
    mode?: MetricArchiveMode
    referenceDate?: string
  }) {
    return rpcClient.getNonArchivedMetrics(organizationId, {
      mode,
      referenceDate
    })
  }

  async getPendingProcessingCount({
    metricId,
    profileId,
    periodStart,
    periodEnd,
    organizationId,
    teamIds
  }: GetPendingProcessingCountParams): Promise<number | undefined> {
    return this.rpcClient.getPendingProcessingCountForMetric(
      metricId,
      profileId,
      periodStart,
      periodEnd,
      organizationId,
      teamIds
    )
  }

  async getOrganizationPendingProcessingCount({
    metricId,
    periodStart,
    periodEnd,
    organizationId
  }: Omit<GetPendingProcessingCountParams, 'profileId' | 'teamIds'>): Promise<number | undefined> {
    return this.rpcClient.getOrganizationPendingProcessingCountForMetric(
      metricId,
      periodStart,
      periodEnd,
      organizationId
    )
  }

  async getSourceRecords({
    metricId,
    profileId,
    periodStart,
    periodEnd,
    organizationId,
    teamIds,
    targetCurrency
  }: GetMetricSourceRecordsParams): Promise<MetricSourceRecord[]> {
    return this.rpcClient.getMetricSourceRecords(
      metricId,
      profileId,
      periodStart,
      periodEnd,
      organizationId,
      teamIds,
      targetCurrency
    )
  }

  async getTargetSourceRecords({
    targetId,
    metricId,
    profileId,
    periodStart,
    periodEnd,
    organizationId,
    targetCurrency
  }: Omit<GetMetricSourceRecordsParams, 'teamIds'> & { targetId: number }): Promise<
    MetricSourceRecord[]
  > {
    return this.rpcClient.getTargetMetricSourceRecords(
      targetId,
      metricId,
      profileId,
      periodStart,
      periodEnd,
      organizationId,
      targetCurrency
    )
  }

  async getTargetSourceRecordsPage({
    targetId,
    metricId,
    profileId,
    periodStart,
    periodEnd,
    organizationId,
    page,
    targetCurrency
  }: GetMetricSourceRecordsParams & {
    targetId: number
    page: TargetContributingSourceRecordsPageParams
  }): Promise<MetricSourceRecordsPage> {
    return this.rpcClient.getTargetMetricSourceRecordsPage(
      targetId,
      metricId,
      profileId,
      periodStart,
      periodEnd,
      organizationId,
      page,
      targetCurrency
    )
  }

  async getOrganizationSourceRecords({
    metricId,
    periodStart,
    periodEnd,
    organizationId,
    targetCurrency
  }: Omit<GetMetricSourceRecordsParams, 'profileId' | 'teamIds'>): Promise<MetricSourceRecord[]> {
    return this.rpcClient.getOrganizationMetricSourceRecords(
      metricId,
      periodStart,
      periodEnd,
      organizationId,
      targetCurrency
    )
  }

  async getOrganizationSourceRecordsPage({
    metricId,
    profileId,
    periodStart,
    periodEnd,
    organizationId,
    page,
    targetCurrency
  }: GetMetricSourceRecordsParams & {
    page: TargetContributingSourceRecordsPageParams
  }): Promise<MetricSourceRecordsPage> {
    return this.rpcClient.getOrganizationMetricSourceRecordsPage(
      metricId,
      periodStart,
      periodEnd,
      organizationId,
      profileId,
      page,
      targetCurrency
    )
  }

  /**
   * Retrieves a metric series for any consumer that needs period-level metric
   * values. Dashboard reports compose this with payouts; they do not query the
   * metric RPC directly.
   */
  getValuesByPeriod(params: GetMetricsByPeriodParams) {
    return this.rpcClient.getMetricsByPeriod(params)
  }

  /**
   * Retrieves metric facts split by their recorded team or profile dimension.
   * The stored dimensions are historical facts and are never reassigned from
   * later membership changes by payout-report callers.
   */
  getValuesByDimension(params: GetMetricValueDimensionsParams) {
    return this.rpcClient.getCompensationHistoryMetricBuckets(params)
  }

  getMetricUsage(): Promise<MetricUsage> {
    if (!this.metric) {
      return Promise.reject(new Error('No metric defined'))
    }
    return this.rpcClient.getMetricUsage(this.metric.id)
  }

  /**
   * Returns the source fields that explain why a record did or did not count
   * for a metric: its configured value, owner/date dimensions, and JSON Logic inputs.
   */
  static getSourceRecordConfiguredFields(metric: Tables<'metrics'>) {
    if (!metric.source) {
      return []
    }

    const filterFields = new Set<string>()
    getFilterFieldNames(metric.filters, filterFields)

    return Array.from(
      new Set(
        [
          ...getFormulaFieldNames(metric),
          metric.user_field,
          metric.accrual_date_field,
          ...filterFields
        ].filter((field): field is string => !!field && isRecordFieldName(field))
      )
    )
  }

  /**
   * Prefer a metric's configured user and accrual dimensions over a source
   * field's storage type, since either dimension can be formula-backed.
   */
  static getSourceRecordConfiguredFieldDisplayType(
    metric: Tables<'metrics'>,
    fieldName: string,
    fieldType?: string
  ) {
    if (fieldName === metric.user_field) {
      return 'user'
    }

    if (fieldName === metric.accrual_date_field) {
      return 'date'
    }

    return fieldType
  }

  static getSourceRecordConfiguredValue(
    metric: Tables<'metrics'>,
    sourceRecord: MetricSourceRecord,
    fieldName: string | RecordFieldReference,
    customFields?: Record<string, Tables<'custom_fields'>>
  ) {
    if (!metric.source || !sourceRecord.source_record) {
      return null
    }

    try {
      return getRecordFieldValue(
        metric.source,
        sourceRecord.source_record as never,
        fieldName,
        customFields
      )
    } catch {
      return null
    }
  }

  static async getDailyMetrics({
    organizationId,
    startDate,
    endDate,
    metricIds = [],
    teamIds = [],
    rpcClient
  }: GetDailyMetricsParams): Promise<DBFunctionReturns<'get_daily_metrics'>> {
    return rpcClient.getDailyMetrics({
      organizationId,
      metricIds,
      teamIds,
      startDate,
      endDate
    })
  }
}
